/**
 * Разрез: где именно нас называют, а где нет.
 *
 * Два уровня, и оба обязательны. **По движкам** — горизонтальные полосы одного
 * момента и малые кратные во времени; девяти цветов у нас нет, движки никогда
 * не кодируются цветом (data-viz.md §5). **По вопросам** — матрица «вопрос ×
 * движок» с четырьмя состояниями, потому что каждая клетка спрашивается дважды
 * и уверенность — это согласие двух прогонов (data-viz.md §6).
 *
 * Здоровье движков стоит здесь же и не для красоты: движок, посыпавшийся
 * ошибками, обязан быть виден строкой, иначе падение видимости объясняют
 * клиенту наугад. Смена модели у движка — внешнее событие, сдвигающее цифры.
 *
 * Внизу — живой ответ движка (`.inst`, foundations.md), единственная тёмная
 * поверхность кабинета. Сам блок общий с обзором бренда и живёт в
 * `ui/LiveAnswer.tsx`: цитата и счётчик ответов обязаны быть одни и те же.
 */

import { useState } from 'react';

import { isoDate } from '@shared/src/intl';

import { useRead, daysAgo } from '../ui/query';
import { WithBrand } from '../ui/screen';
import { LiveAnswer, useAnswers } from '../ui/LiveAnswer';
import { matrixBasis, snapshotBasis } from '../ui/basis';
import { useCells, type CellsReading, type CellRow } from '../ui/cells';
import { CellCard, cellWord, type PerCell } from '../ui/CellCard';
import { byEngineOrder, engineTitle, engineTitles } from '../ui/controls';
import { ErrorNotice, Skeleton } from '../ui/state';
import { Bars, Matrix, SmallMultiples, Stamp } from '../ui/viz';
import type { BarItem, CellState, MatrixRow } from '../ui/viz';
import {
  confidenceShare,
  confidenceWord,
  coverage,
  coverageOf,
  dateNumeric,
  dateProse,
  NOT_MEASURED,
  percent,
} from '@shared/src/format';
import type { components } from '../api/schema';

type EngineWithHealth = components['schemas']['EngineWithHealth'];
type BrandOverview = components['schemas']['BrandOverview'];
type DailySnapshot = components['schemas']['DailySnapshot'];

/** Сколько вопросов помещается в матрицу на одну страницу (data-viz.md §6). */
const MATRIX_ROWS = 50;

export function Engines() {
  return <WithBrand title="Движки">{(brandId) => <Body brandId={brandId} />}</WithBrand>;
}

function Body({ brandId }: { brandId: string }) {
  const from = daysAgo(30);
  const to = isoDate(new Date());

  const engines = useRead<EngineWithHealth[]>('/engines');
  const overview = useRead<BrandOverview>(`/brands/${brandId}/overview`);
  const daily = useRead<DailySnapshot[]>(`/brands/${brandId}/metrics/daily`, {
    from,
    to,
    basis: 'weekly',
    complete_only: true,
  });
  // Клетки матрицы считаются по ОДНОМУ завершённому замеру и своей выборкой
  // (`ui/cells.ts`). Выборка цитаты для них не годится: она держит одну
  // страницу в 200 ответов и ничего не знает о прогоне, поэтому матрица на ней
  // показывала «не измерено» по измеренным вопросам и складывала в клетку
  // попытки разных недель.
  const matrix = useCells(brandId);
  const answers = useAnswers(brandId);

  if (overview.kind === 'failed') return <ErrorNotice error={overview.error} />;
  if (overview.kind === 'loading' || engines.kind === 'loading')
    return <Skeleton rows={3} />;

  const catalogue = engines.kind === 'ready' ? engines.data : [];
  const titles = engineTitles(catalogue);
  const order = byEngineOrder(catalogue);
  const active = catalogue.filter((engine) => engine.is_active);

  const rows = overview.data.engines ?? [];
  const byCode = new Map(rows.map((row) => [row.engine_code, row]));
  const codes = (
    active.length
      ? active.map((engine) => engine.code)
      : rows.map((row) => row.engine_code)
  ).sort(order);

  const bars: BarItem[] = codes.map((code) => {
    const row = byCode.get(code);
    const has = row && (row.organic_answers ?? 0) > 0;
    return {
      key: code,
      name: engineTitle(titles, code),
      note: has
        ? `назван в ${coverageOf(row.organic_mentions ?? 0, row.organic_answers ?? 0, 'ответ', 'ответа', 'ответов')}`
        : 'движок не ответил в этом прогоне',
      value: has ? (row.visibility ?? null) : null,
    };
  });

  const series = daily.kind === 'ready' ? daily.data : [];
  const cells = codes.map((code) => {
    const points = series
      .filter((point) => point.engine_code === code)
      .sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date))
      .map((point) => ({
        date: point.snapshot_date,
        value: point.visibility ?? null,
        incomplete: !point.is_complete,
      }));
    const row = byCode.get(code);
    return {
      key: code,
      name: engineTitle(titles, code),
      points,
      value: row && (row.organic_answers ?? 0) > 0 ? (row.visibility ?? null) : null,
    };
  });

  return (
    <>
      {/* Основание экрана — одной строкой и один раз, той же фразой, что на
          обзоре (`ui/basis.ts`). Одной даты мало: полнота сетки с экрана
          пропадала совсем, и закрытая неполная точка выглядела полной. */}
      <Stamp>
        {snapshotBasis({
          date: overview.data.snapshot_date,
          rows,
          codes,
          gridComplete: overview.data.grid_complete,
        })}
      </Stamp>

      <section className="block">
        <h2>Где нас называют прямо сейчас</h2>
        <Bars items={bars} />
      </section>

      <section className="block">
        <h2>Как это менялось</h2>
        {daily.kind === 'loading' ? (
          <Skeleton rows={2} />
        ) : (
          <SmallMultiples cells={cells} />
        )}
      </section>

      <QuestionMatrix brandId={brandId} cells={matrix} codes={codes} titles={titles} />

      <EngineHealth engines={catalogue} rows={rows} titles={titles} codes={codes} />

      <LiveAnswer brandId={brandId} answers={answers} titles={titles} />
    </>
  );
}

/* ─────────────────────── матрица «вопрос × движок» ────────────────────────── */

function QuestionMatrix({
  brandId,
  cells,
  codes,
  titles,
}: {
  brandId: string;
  cells: CellsReading;
  codes: string[];
  titles: Map<string, string>;
}) {
  // Выбранная клетка держится ключами вопроса и движка, а не ссылкой на
  // прочитанную строку: строки перечитываются после нового замера, и удержанная
  // ссылка оставила бы на экране окно от прошлого чтения. Вместе с ключами
  // держится и то, ЧЬЁ это чтение: экран не размонтируется ни при смене бренда,
  // ни при новом замере, и без этой привязки открытое окно доживало бы до
  // чужих данных и молча показывало бы их под прежним заголовком.
  const [picked, setPicked] = useState<{
    of: string;
    row: string;
    code: string;
  } | null>(null);

  if (cells.kind === 'failed') return <ErrorNotice error={cells.error} />;
  if (cells.kind === 'loading') {
    return (
      <section className="block">
        <h2>По каким вопросам вас не называют</h2>
        <Skeleton rows={2} />
      </section>
    );
  }

  const { run, rows: measured, complete } = cells.data;
  const perCell: PerCell = run?.runs_per_cell ?? 2;
  const shown = measured.slice(0, MATRIX_ROWS);

  // Клетка — это вопрос × движок и **два прогона** одного замера. Состояние
  // считается по числу попыток и по тому, в скольких из них бренд назван: три
  // состояния уверенность выразить не могут. «Не измерено» теперь означает
  // ровно то, что написано: движок по этому вопросу в этом замере не ответил.
  //
  // Полная заливка стоит только там, где СОШЛИСЬ ДВА прогона. Клетка, где
  // удался один и он назвал бренд, красится половиной: согласия в ней нет, и
  // выдавать её за подтверждённую значит повторять на цвете то самое враньё,
  // которое поправлено в словах.
  const rows: MatrixRow[] = shown.map((row) => ({
    key: row.id,
    label: row.text,
    cells: codes.map((code) => {
      const cell = row.cells.get(code);
      let state: CellState = 'na';
      if (cell && cell.tries > 0) {
        if (cell.hits === 0) state = 'none';
        else if (cell.hits >= cell.tries && cell.tries >= perCell) state = 'both';
        else state = 'one';
      }
      return { key: code, state, word: cellWord(cell, perCell) };
    }),
  }));

  const of = `${brandId}|${run?.id ?? ''}`;
  const open: { row: CellRow; code: string } | null =
    picked && picked.of === of
      ? (() => {
          const row = shown.find((item) => item.id === picked.row);
          return row && codes.includes(picked.code) ? { row, code: picked.code } : null;
        })()
      : null;

  return (
    <section className="block">
      <h2>По каким вопросам нас не называют</h2>
      {rows.length === 0 ? (
        <p className="stamp">
          {run
            ? 'Замер закрылся без разобранных ответов. Матрица появится после следующего.'
            : 'Завершённых замеров пока нет. Матрица появится после первого.'}
        </p>
      ) : (
        <div style={{ ['--cols' as string]: String(codes.length) }}>
          <Matrix
            columns={codes.map((code) => ({
              key: code,
              name: engineTitle(titles, code),
            }))}
            rows={rows}
            when={run?.snapshot_date ? dateProse(run.snapshot_date) : undefined}
            caption={matrixBasis({
              run,
              shown: rows.length,
              measured: measured.length,
              complete,
            })}
            onPick={(rowKey, colKey) => setPicked({ of, row: rowKey, code: colKey })}
          />
        </div>
      )}

      {open ? (
        <CellCard
          question={open.row.text}
          engine={engineTitle(titles, open.code)}
          cell={open.row.cells.get(open.code)}
          perCell={perCell}
          when={run?.snapshot_date ?? null}
          complete={complete}
          onClose={() => setPicked(null)}
        />
      ) : null}
    </section>
  );
}

/* ─────────────────────────── здоровье движков ─────────────────────────────── */

function EngineHealth({
  engines,
  rows,
  titles,
  codes,
}: {
  engines: EngineWithHealth[];
  rows: DailySnapshot[];
  titles: Map<string, string>;
  codes: string[];
}) {
  const byCode = new Map(rows.map((row) => [row.engine_code, row]));
  const health = new Map(engines.map((engine) => [engine.code, engine]));

  return (
    <section className="block">
      <h2>Состояние движков</h2>
      <div className="tbl-wrap">
        <table className="mini">
          {/* Легенда называет причину «Не измерено» ОТДЕЛЬНО ДЛЯ КАЖДОЙ
              колонки: прежняя объясняла все разом одной причиной («движок не
              дал ни одного органического ответа»), а это верно только для
              согласия прогонов. У доли успешных обращений причина своя —
              к движку за час не обращались. */}
          <caption>Движки и их состояние</caption>
          <thead>
            <tr>
              <th scope="col">Движок</th>
              <th scope="col">Состояние</th>
              <th
                scope="col"
                className="num"
                title="Доля успешных обращений за последний час"
              >
                Успешных за час
              </th>
              {/* Не «уверенность». Это согласие двух прогонов одной клетки между
                  собой — утверждение об устойчивости ответа нейросети, а не о том,
                  насколько мы уверены в замере, и не о качестве разбора, которое
                  стоит соседней колонкой. Под прежним словом клиент читал
                  «Seeno уверен в этих числах на 62%». */}
              <th
                scope="col"
                className="num"
                title="Насколько сошлись два прогона одной клетки между собой"
              >
                Согласие прогонов
              </th>
              {/* Качество разбора: `scan.analyses.parser` наконец виден клиенту.
                  Запасной разбор честно означает «нашли вхождение» и места не
                  ставит — без этой колонки доля «в топ-3» у движка неотличима от
                  доли, посчитанной по одному упоминанию из шести. */}
              <th
                scope="col"
                className="num"
                title="Сколько ответов прочитала нейросеть, а сколько — запасной поиск вхождения"
              >
                Как прочитан
              </th>
              <th scope="col">Источники</th>
              <th scope="col">Смена нейросети</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => {
              const engine = health.get(code);
              const row = byCode.get(code);
              const answered = row && (row.organic_answers ?? 0) > 0;
              const confidence = row?.confidence ?? null;
              const fallback = answered ? (row?.fallback_analyses ?? 0) : null;
              return (
                <tr key={code}>
                  <td>{engineTitle(titles, code)}</td>
                  <td>
                    {engine?.is_available === false
                      ? 'Недоступен'
                      : answered
                        ? 'Отвечает'
                        : 'Не ответил в этом прогоне'}
                  </td>
                  <td className="tnum num">
                    {engine?.success_rate_1h === null ||
                    engine?.success_rate_1h === undefined
                      ? NOT_MEASURED
                      : percent(Math.round(engine.success_rate_1h * 100))}
                  </td>
                  <td
                    className="tnum num"
                    title={
                      confidence === null
                        ? undefined
                        : `Согласие прогонов ${confidenceShare(confidence)}`
                    }
                  >
                    {confidence === null ? NOT_MEASURED : confidenceWord(confidence)}
                  </td>
                  <td
                    className="tnum num"
                    title={
                      fallback === null
                        ? undefined
                        : 'Запасное чтение находит имя, но не ставит место в списке'
                    }
                  >
                    {fallback === null
                      ? NOT_MEASURED
                      : fallback === 0
                        ? 'Нейросетью'
                        : `Поиском ${coverage(fallback, row?.organic_answers ?? 0)}`}
                  </td>
                  <td>
                    {engine?.supports_citations ? 'Отдаёт ссылки' : 'Ссылок не отдаёт'}
                  </td>
                  <td className="tnum">
                    {engine?.last_model_change_at
                      ? dateNumeric(engine.last_model_change_at)
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
